import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { ColumnGroup } from "primereact/columngroup";
import { DataTable } from "primereact/datatable";
import { Row } from "primereact/row";
import { useMemo, useState } from "react";
import { useLazyGetOrderBalanceQuery } from "store/queries/orderBalance";
import styled from "styled-components";
import {
  IOrderBalance,
  IOrderBalanceBoxRow,
  IOrderBalanceEvent,
  IOrderBalanceInvariant,
  IOrderBalanceParty,
  IOrderBalanceReportRow,
  IOrderBalanceTransferRow,
} from "types/OrderBalance";

import {
  EventTableRow,
  buildEventRows,
  formatCents,
  formatDate,
  partyField,
  sumField,
} from "./helpers";

const StyledContainer = styled.div`
  background: var(--white-100, #fff);
  border: 1px solid var(--gray-200, #e5e7eb);
  border-radius: 8px;
  padding: 16px;

  h4 {
    margin: 20px 0 8px;
  }

  .p-datatable {
    font-size: 0.85rem;
  }

  .balance-nonzero {
    background: #fde8e8;
    color: #9b1c1c;
    font-weight: 600;
  }

  .money {
    text-align: right;
    white-space: nowrap;
  }
`;

const StatusIcon = styled.i<{ $status: string }>`
  margin-right: 8px;
  color: ${({ $status }) => {
    if ($status === "PASS") return "#057a55";
    if ($status === "FAIL") return "#c81e1e";
    return "#6b7280";
  }};
`;

const statusIcon = (status: string) => {
  if (status === "PASS") return "pi pi-check-circle";
  if (status === "FAIL") return "pi pi-times-circle";
  return "pi pi-minus-circle";
};

const moneyCell = (value: number | null | undefined, highlight = false) => (
  <span className={highlight && value ? "balance-nonzero" : undefined}>
    {formatCents(value)}
  </span>
);

// Cell templates live at module scope (react/no-unstable-nested-components).
const accountBody = (row: IOrderBalanceBoxRow) =>
  row.kind === "TOTAL" ? <strong>{row.name}</strong> : row.name;

const eventTypeBody = (row: EventTableRow) => (
  <span title={(row.notes as string) || undefined}>
    {row.type}
    {row.label ? ` — ${row.label}` : ""}
  </span>
);

const verifiedBody = (row: { verified: unknown; verificationNote?: string }) =>
  row.verified ? (
    <i
      className="pi pi-check"
      title={row.verificationNote || "Verified at the processor"}
    />
  ) : (
    <span title={row.verificationNote || "Not verified at the processor"}>
      DB only
    </span>
  );

type ReportTableRow = IOrderBalanceReportRow & { partyName: string };

const reportGroupHeader = (row: ReportTableRow) => (
  <strong>{row.partyName}</strong>
);

const reportIdBody = (row: ReportTableRow) => (
  <span>
    {row.reportId}
    {row.isHidden ? " (hidden)" : ""}
    {row.publishedAt ? "" : " (unpublished)"}
  </span>
);

function BalanceBoxTable({ ledger }: { ledger: IOrderBalance }) {
  const { balanceBox } = ledger;
  const rows = [...balanceBox.rows, balanceBox.total];
  const balanceColumn = (field: keyof IOrderBalanceBoxRow, header: string) => (
    <Column
      header={header}
      bodyClassName="money"
      body={(row: IOrderBalanceBoxRow) =>
        moneyCell(row[field] as number | null, true)
      }
    />
  );
  const plainColumn = (field: keyof IOrderBalanceBoxRow, header: string) => (
    <Column
      header={header}
      bodyClassName="money"
      body={(row: IOrderBalanceBoxRow) =>
        moneyCell(row[field] as number | null)
      }
    />
  );
  return (
    <>
      <DataTable value={rows} dataKey="partyId" size="small">
        <Column header="Account" body={accountBody} />
        {plainColumn("providerOwedInCents", "Provider Owed")}
        {plainColumn("providerPaidInCents", "Provider Paid")}
        {plainColumn("reportedPaidInCents", "Reported Paid")}
        {plainColumn("providerTotalInCents", "Provider Total")}
        {plainColumn("reportedTotalInCents", "Reported Total")}
        {balanceColumn("cashBalanceInCents", "Cash Balance")}
        {balanceColumn("reportedCashBalanceInCents", "Reported Cash Balance")}
        {balanceColumn("reportedTppBalanceInCents", "Reported TPP Balance")}
      </DataTable>
      <div className="flex gap-4 mt-2">
        <span>
          Patient Paid:{" "}
          <strong>{formatCents(balanceBox.patientPaidInCents)}</strong>
        </span>
        <span>
          Patient Balance: {moneyCell(balanceBox.patientBalanceInCents, true)}
        </span>
        {balanceBox.unallocatedInCents !== 0 && (
          <span>
            Unallocated on the platform:{" "}
            {moneyCell(balanceBox.unallocatedInCents, true)}
          </span>
        )}
      </div>
    </>
  );
}

function EventTable({
  events,
  providers,
  showVerification,
}: {
  events: IOrderBalanceEvent[];
  providers: IOrderBalanceParty[];
  showVerification: boolean;
}) {
  const rows = useMemo(
    () => buildEventRows(events, providers),
    [events, providers]
  );
  const fields: Array<"total" | "provider" | "fee"> = [
    "total",
    "provider",
    "fee",
  ];
  const fieldHeaders = {
    total: "Total",
    provider: "Provider",
    fee: "Clear Fee",
  };

  const header = (
    <ColumnGroup>
      <Row>
        <Column header="Date" rowSpan={2} />
        <Column header="Type" rowSpan={2} />
        <Column
          header={showVerification ? "Patient" : "Order Total"}
          rowSpan={2}
        />
        {showVerification && <Column header="Verified" rowSpan={2} />}
        {providers.map((p) => (
          <Column key={p.id} header={p.name} colSpan={3} />
        ))}
        <Column header="Clear Health (direct)" rowSpan={2} />
        {showVerification && <Column header="Unallocated" rowSpan={2} />}
      </Row>
      <Row>
        {providers.flatMap((p) =>
          fields.map((f) => (
            <Column key={`${p.id}-${f}`} header={fieldHeaders[f]} />
          ))
        )}
      </Row>
    </ColumnGroup>
  );

  const footer = (
    <ColumnGroup>
      <Row>
        <Column footer="Total" colSpan={2} />
        <Column footer={formatCents(sumField(rows, "patient"))} />
        {showVerification && <Column footer="" />}
        {providers.flatMap((p) =>
          fields.map((f) => (
            <Column
              key={`${p.id}-${f}-total`}
              footer={formatCents(sumField(rows, partyField(p.id, f)))}
            />
          ))
        )}
        <Column footer={formatCents(sumField(rows, "clearDirect"))} />
        {showVerification && (
          <Column footer={formatCents(sumField(rows, "unallocated"))} />
        )}
      </Row>
    </ColumnGroup>
  );

  return (
    <DataTable
      value={rows}
      dataKey="id"
      size="small"
      headerColumnGroup={header}
      footerColumnGroup={footer}
      emptyMessage="Nothing recorded."
      scrollable
      scrollDirection="horizontal"
    >
      <Column
        field="date"
        body={(row: EventTableRow) => formatDate(row.date as string)}
      />
      <Column field="type" body={eventTypeBody} />
      <Column
        field="patient"
        bodyClassName="money"
        body={(row: EventTableRow) => formatCents(row.patient as number)}
      />
      {showVerification && <Column field="verified" body={verifiedBody} />}
      {providers.flatMap((p) =>
        fields.map((f) => (
          <Column
            key={`${p.id}-${f}-body`}
            field={partyField(p.id, f)}
            bodyClassName="money"
            body={(row: EventTableRow) =>
              formatCents(row[partyField(p.id, f)] as number | null)
            }
          />
        ))
      )}
      <Column
        field="clearDirect"
        bodyClassName="money"
        body={(row: EventTableRow) =>
          row.clearDirect ? formatCents(row.clearDirect as number) : ""
        }
      />
      {showVerification && (
        <Column
          field="unallocated"
          bodyClassName="money"
          body={(row: EventTableRow) =>
            moneyCell(row.unallocated as number, true)
          }
        />
      )}
    </DataTable>
  );
}

function ReportsTable({
  rows,
  partyName,
}: {
  rows: IOrderBalanceReportRow[];
  partyName: (id: string) => string;
}) {
  const tableRows: ReportTableRow[] = rows.map((row) => ({
    ...row,
    partyName: partyName(row.providerId),
  }));
  return (
    <DataTable
      value={tableRows}
      size="small"
      emptyMessage="No weekly report covers this order yet."
      rowGroupMode="subheader"
      groupRowsBy="providerId"
      rowGroupHeaderTemplate={reportGroupHeader}
    >
      <Column
        header="Report date"
        body={(r: IOrderBalanceReportRow) => formatDate(r.reportDate)}
      />
      <Column
        header="Amount Paid"
        bodyClassName="money"
        body={(r: IOrderBalanceReportRow) => formatCents(r.amountPaidInCents)}
      />
      <Column
        header="Clear Fee Withheld"
        bodyClassName="money"
        body={(r: IOrderBalanceReportRow) =>
          formatCents(r.clearFeeWithheldInCents)
        }
      />
      <Column
        header="Total Patient Payment"
        bodyClassName="money"
        body={(r: IOrderBalanceReportRow) =>
          formatCents(r.totalPatientPaymentInCents)
        }
      />
      <Column header="Date Paid" field="datePaid" />
      <Column header="Provider to provider" field="providerToProvider" />
      <Column header="Processor" field="processor" />
      <Column header="Notes" field="notes" />
      <Column header="Report" body={reportIdBody} />
    </DataTable>
  );
}

function TransferRowsTable({
  rows,
  partyName,
}: {
  rows: IOrderBalanceTransferRow[];
  partyName: (id: string | null) => string;
}) {
  return (
    <DataTable
      value={rows}
      dataKey="id"
      size="small"
      emptyMessage="No processor rows."
    >
      <Column
        header="Created"
        body={(r: IOrderBalanceTransferRow) => formatDate(r.createdAt)}
      />
      <Column
        header="Party"
        body={(r: IOrderBalanceTransferRow) => partyName(r.providerId)}
      />
      <Column header="Kind" field="kind" />
      <Column
        header="Amount (DB)"
        bodyClassName="money"
        body={(r: IOrderBalanceTransferRow) => formatCents(r.amountInCents)}
      />
      <Column
        header="Clear Fee (DB)"
        bodyClassName="money"
        body={(r: IOrderBalanceTransferRow) =>
          formatCents(r.clearFeeAmountInCents)
        }
      />
      <Column header="Processor id" field="processorId" />
      <Column
        header="Processor"
        bodyClassName="money"
        body={(r: IOrderBalanceTransferRow) =>
          r.processorAmountInCents === null
            ? ""
            : `${formatCents(r.processorAmountInCents)}${
                r.processorAmountReversedInCents
                  ? ` (reversed ${formatCents(
                      r.processorAmountReversedInCents
                    )})`
                  : ""
              }`
        }
      />
      <Column header="Verified" body={verifiedBody} />
      <Column header="Report" field="reportId" />
    </DataTable>
  );
}

function InvariantList({
  invariants,
}: {
  invariants: IOrderBalanceInvariant[];
}) {
  return (
    <ul className="list-none p-0 m-0">
      {invariants.map((check) => (
        <li
          key={check.name}
          className="mb-2"
          data-testid={`invariant-${check.name}`}
        >
          <StatusIcon
            $status={check.status}
            className={statusIcon(check.status)}
            aria-label={check.status}
          />
          <strong>{check.title}</strong> — {check.status}: {check.message}
          {check.details.length > 0 && (
            <ul className="mt-1 mb-0">
              {check.details.map((detail) => (
                <li key={detail}>{detail}</li>
              ))}
            </ul>
          )}
        </li>
      ))}
    </ul>
  );
}

/**
 * CLE-2849: SuperAdmin-only per-order balance ledger — Ryan's reconciliation workbook in the product.
 * Collapsed and idle until "Run balance check": the endpoint reads Stripe/Finix live, so it must
 * never fire on page open.
 */
export function OrderBalance({ orderId }: { orderId: string }) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [trigger, { data, isFetching, error }] = useLazyGetOrderBalanceQuery();
  const ledger = data?.data;

  const providers = useMemo(
    () => (ledger?.parties ?? []).filter((p) => p.kind === "PROVIDER"),
    [ledger]
  );
  const partyName = (id: string | null) =>
    ledger?.parties.find((p) => p.id === id)?.name ??
    id ??
    "Clear Health / patient";

  const run = () => {
    setExpanded(true);
    setCopied(false);
    trigger(orderId);
  };

  const copyJson = () => {
    if (!ledger) return;
    navigator.clipboard
      ?.writeText(JSON.stringify(ledger, null, 2))
      .then(() => setCopied(true))
      .catch(() => setCopied(false));
  };

  const failing =
    ledger?.invariants.filter((i) => i.status === "FAIL").length ?? 0;

  return (
    <StyledContainer data-testid="order-balance">
      <div className="flex align-items-center justify-content-between gap-2 flex-wrap">
        <button
          type="button"
          className="p-link flex align-items-center gap-2"
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
        >
          <i
            className={expanded ? "pi pi-chevron-down" : "pi pi-chevron-right"}
          />
          <h3 className="m-0">Order balance (SuperAdmin)</h3>
        </button>
        <div className="flex gap-2">
          {ledger && (
            <Button
              label={copied ? "Copied" : "Copy JSON"}
              icon="pi pi-copy"
              className="p-button-outlined p-button-sm"
              onClick={copyJson}
            />
          )}
          <Button
            label={ledger ? "Re-run balance check" : "Run balance check"}
            icon="pi pi-calculator"
            className="p-button-sm"
            loading={isFetching}
            onClick={run}
          />
        </div>
      </div>

      {expanded && (
        <div className="mt-3">
          {!ledger && !isFetching && !error && (
            <p className="m-0">
              Compares what should have happened, what the processor did and
              what the weekly reports said. Reads Stripe/Finix live, so it only
              runs when you ask.
            </p>
          )}
          {error && (
            <p className="m-0 text-red-600">
              The balance check failed:{" "}
              {(error as any)?.data?.message ??
                (error as any)?.status ??
                "unknown error"}
            </p>
          )}
          {ledger && (
            <>
              <p className="m-0">
                Order <code>{ledger.orderId}</code> · Clear fee{" "}
                {ledger.clearFeeRatePercent === null
                  ? "n/a"
                  : `${ledger.clearFeeRatePercent}%`}{" "}
                · {ledger.processors.join(" + ") || "no processor"} ·{" "}
                {ledger.clearKeepsFeeOnRefund
                  ? "Clear keeps its fee on refunds"
                  : "refunds return Clear's fee"}{" "}
                · run {new Date(ledger.generatedAt).toLocaleString("en-US")}
              </p>
              <p className="mt-1 mb-0" data-testid="order-balance-verdict">
                {ledger.balanceBox.allZero && failing === 0 ? (
                  <strong style={{ color: "#057a55" }}>
                    In balance: every balance is zero.
                  </strong>
                ) : (
                  <strong style={{ color: "#c81e1e" }}>
                    Out of balance — {failing} check{failing === 1 ? "" : "s"}{" "}
                    failing.
                  </strong>
                )}
              </p>

              <h4>Summary</h4>
              <BalanceBoxTable ledger={ledger} />

              <h4>Order Overview — what was supposed to happen</h4>
              <EventTable
                events={ledger.orderOverview}
                providers={providers}
                showVerification={false}
              />

              <h4>Payment Activity — what happened at the processor</h4>
              <EventTable
                events={ledger.paymentActivity}
                providers={providers}
                showVerification
              />

              <h4>Reconciliation Reports — what the weekly reports said</h4>
              <ReportsTable
                rows={ledger.reconciliationReports.rows}
                partyName={partyName}
              />

              <h4>Checks</h4>
              <InvariantList invariants={ledger.invariants} />

              <h4>Processor rows</h4>
              {ledger.processorVerification.errors.length > 0 && (
                <ul className="mt-0">
                  {ledger.processorVerification.errors.map((e) => (
                    <li key={e}>{e}</li>
                  ))}
                </ul>
              )}
              <TransferRowsTable
                rows={ledger.transferRows}
                partyName={partyName}
              />
            </>
          )}
        </div>
      )}
    </StyledContainer>
  );
}

export default OrderBalance;
